import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FocusTrap from '@mui/material/Unstable_TrapFocus';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import TuneIcon from '@mui/icons-material/Tune';
import UndoIcon from '@mui/icons-material/Undo';
import { AiImagePreset, AiImageStudioConfig, AiImageStudioDraft } from '../types';
import { optimizeAiImagePrompt } from '../aiImageStudioApi';
import { estimateAiImageCostText } from '../costEstimate';
import {
  IMAGE_QUALITY_HELP,
  IMAGE_SIZE_HELP,
  formatOpenAiQualityLabel,
  getSelectableStudioModels,
  resolveSelectableModelId,
} from '../modelCatalog';

interface PromptEditorProps {
  config: AiImageStudioConfig | null;
  presets: AiImagePreset[];
  draft: AiImageStudioDraft;
  generating: boolean;
  showPreset?: boolean;
  presetLabel?: string;
  blockedReason?: string;
  onModelChange: (value: string) => void;
  onPromptChange: (value: string) => void;
  onPresetChange: (presetId: string) => void;
  onAspectRatioChange: (value: string) => void;
  onImageSizeChange: (value: string) => void;
  onQualityChange: (value: string) => void;
  onOutputFormatChange: (value: string) => void;
  onCountChange: (value: number) => void;
  onGenerate: () => void;
  onResetPrompt?: () => void;
  resetPromptLabel?: string;
  children?: React.ReactNode;
}

function getOutputFormatLabel(format: string, supportsTransparentBackground: boolean): string {
  if (format === 'jpeg') return 'JPG';
  if (format === 'png') return supportsTransparentBackground ? 'PNG (transparent)' : 'PNG';
  return format.toUpperCase();
}

const cardSx = {
  borderRadius: 3,
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'rgba(15,23,42,0.08)',
  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
} as const;

const fieldLabelSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  mb: 0.625,
  minHeight: 20,
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.01em',
  color: 'text.secondary',
} as const;

const selectSx = {
  borderRadius: 2,
  bgcolor: '#f8fafc',
  fontSize: '0.9rem',
  '& .MuiSelect-select': { py: 1.1 },
  '& fieldset': { borderColor: 'rgba(15,23,42,0.12)' },
  '&:hover fieldset': { borderColor: 'rgba(15,23,42,0.28)' },
  '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
} as const;

const roundToolButtonSx = {
  width: 32,
  height: 32,
  p: 0,
  borderRadius: '50%',
  flexShrink: 0,
  border: '1px solid',
  borderColor: 'rgba(15,23,42,0.12)',
  bgcolor: 'background.paper',
  color: 'text.secondary',
  '&:hover': { bgcolor: 'rgba(15,52,96,0.06)', color: 'primary.main', borderColor: 'rgba(15,52,96,0.3)' },
  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
} as const;

const promptInputRootSx = {
  borderRadius: 2.5,
  bgcolor: 'background.paper',
  fontSize: '0.92rem',
  lineHeight: 1.55,
  alignItems: 'flex-start',
  '& fieldset': { borderColor: 'rgba(15,23,42,0.12)' },
  '&:hover fieldset': { borderColor: 'rgba(15,23,42,0.28)' },
  '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
} as const;

const promptTextareaSx = {
  resize: 'none',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { width: 5 },
  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.15)', borderRadius: 4 },
} as const;

const SettingField: React.FC<{
  id: string;
  label: string;
  help?: string;
  fullRow?: boolean;
  children: React.ReactNode;
}> = ({ id, label, help, fullRow, children }) => (
  <Box sx={{ minWidth: 0, gridColumn: fullRow ? '1 / -1' : undefined }}>
    <Typography id={id} component="div" sx={fieldLabelSx}>
      {label}
      {help && (
        <Tooltip title={help} enterDelay={200}>
          <InfoOutlinedIcon
            tabIndex={0}
            aria-label={`Om ${label.toLowerCase()}`}
            sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', borderRadius: '50%' } }}
          />
        </Tooltip>
      )}
    </Typography>
    {children}
  </Box>
);

const PromptEditor: React.FC<PromptEditorProps> = ({
  config,
  presets,
  draft,
  generating,
  showPreset = true,
  presetLabel = 'Promptmall',
  blockedReason,
  onModelChange,
  onPromptChange,
  onPresetChange,
  onAspectRatioChange,
  onImageSizeChange,
  onQualityChange,
  onOutputFormatChange,
  onCountChange,
  onGenerate,
  onResetPrompt,
  resetPromptLabel = 'Återställ prompt',
  children,
}) => {
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeError, setOptimizeError] = useState<string | null>(null);
  const [promptBeforeOptimize, setPromptBeforeOptimize] = useState<string | null>(null);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const expandedPromptInputRef = useRef<HTMLTextAreaElement>(null);
  const pickerModels = getSelectableStudioModels(config);
  const selectedModelId = resolveSelectableModelId(draft.model, config);
  const isOpenAiModel = draft.provider === 'openai' || pickerModels.find((model) => model.id === selectedModelId)?.provider === 'openai';
  const selectedModelConfig = pickerModels.find((model) => model.id === selectedModelId) || null;

  useEffect(() => {
    if (selectedModelId && selectedModelId !== draft.model) {
      onModelChange(selectedModelId);
    }
  }, [draft.model, onModelChange, selectedModelId]);

  useEffect(() => {
    if (draft.count !== 1) {
      onCountChange(1);
    }
  }, [draft.count, onCountChange]);

  useEffect(() => {
    if (!promptExpanded) return;
    const input = expandedPromptInputRef.current;
    if (!input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [promptExpanded]);

  const outputFormats = selectedModelConfig?.outputFormats?.length
    ? selectedModelConfig.outputFormats
    : isOpenAiModel
      ? (config?.openAiOutputFormats || ['jpeg', 'png', 'webp'])
      : ['jpeg'];
  const selectedOutputFormat = outputFormats.includes(draft.outputFormat || '')
    ? (draft.outputFormat || 'jpeg')
    : (outputFormats[0] || 'jpeg');
  const showOutputFormat = outputFormats.length > 1;
  const supportsTransparentBackground = Boolean(selectedModelConfig?.supportsTransparentBackground);
  const costEstimateText = estimateAiImageCostText(config, draft);
  const promptBlockedReason = blockedReason || (!draft.prompt.trim() ? 'Skriv en prompt först.' : undefined);
  const qualityAndFormatCount = Number(isOpenAiModel) + Number(showOutputFormat);

  const handleOptimizePrompt = async () => {
    if (!draft.prompt.trim() || optimizing) return;
    setOptimizing(true);
    setOptimizeError(null);
    const currentPrompt = draft.prompt;
    try {
      const optimized = await optimizeAiImagePrompt(currentPrompt, selectedModelId || draft.model);
      setPromptBeforeOptimize(currentPrompt);
      onPromptChange(optimized);
    } catch (error: any) {
      setOptimizeError(error?.response?.data?.message || error?.message || 'Prompten kunde inte optimeras.');
    } finally {
      setOptimizing(false);
    }
  };

  const handlePromptInput = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPromptBeforeOptimize(null);
    onPromptChange(event.target.value);
  };

  const optimizeButton = (
    <Tooltip title="Optimera prompt med AI">
      <span>
        <IconButton
          aria-label="Optimera prompt med AI"
          disabled={!draft.prompt.trim() || optimizing}
          onClick={() => void handleOptimizePrompt()}
          sx={{
            ...roundToolButtonSx,
            color: 'primary.main',
            borderColor: 'rgba(15,52,96,0.25)',
            bgcolor: 'rgba(15,52,96,0.06)',
          }}
        >
          {optimizing ? <CircularProgress size={16} /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </span>
    </Tooltip>
  );

  const optimizeFeedback = (optimizeError || promptBeforeOptimize) ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {optimizeError && <Alert severity="error" sx={{ py: 0, flex: 1 }}>{optimizeError}</Alert>}
      {promptBeforeOptimize && (
        <Button
          size="small"
          startIcon={<UndoIcon />}
          onClick={() => {
            onPromptChange(promptBeforeOptimize);
            setPromptBeforeOptimize(null);
          }}
          sx={{ textTransform: 'none' }}
        >
          Ångra optimering
        </Button>
      )}
    </Box>
  ) : null;

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        aria-hidden={promptExpanded || undefined}
        sx={{
          flex: 1,
          minHeight: 0,
          height: '100%',
          overflow: 'auto',
          px: { xs: 2, sm: 3 },
          py: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {children}

        <Box component="section" aria-labelledby="ai-image-settings-heading" sx={{ ...cardSx, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
            <TuneIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography id="ai-image-settings-heading" component="h2" sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'text.primary' }}>
              Bildinställningar
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 1.5, rowGap: 1.75 }}>
            <SettingField id="ai-image-model-label" label="Modell" fullRow>
              <Select
                size="small"
                fullWidth
                labelId="ai-image-model-label"
                value={selectedModelId || draft.model}
                onChange={(event) => onModelChange(String(event.target.value))}
                sx={selectSx}
              >
                {pickerModels.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </SettingField>

            <SettingField id="ai-image-ratio-label" label="Bildförhållande">
              <Select
                size="small"
                fullWidth
                labelId="ai-image-ratio-label"
                value={draft.aspectRatio}
                onChange={(event) => onAspectRatioChange(String(event.target.value))}
                sx={selectSx}
              >
                {(config?.aspectRatios || []).map((ratio) => (
                  <MenuItem key={ratio} value={ratio}>
                    {ratio}
                  </MenuItem>
                ))}
              </Select>
            </SettingField>

            <SettingField id="ai-image-size-label" label="Storlek" help={IMAGE_SIZE_HELP}>
              <Select
                size="small"
                fullWidth
                labelId="ai-image-size-label"
                value={draft.imageSize}
                onChange={(event) => onImageSizeChange(String(event.target.value))}
                sx={selectSx}
              >
                {(config?.imageSizes || []).map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </SettingField>

            {isOpenAiModel && (
              <SettingField id="ai-image-quality-label" label="Kvalitet" help={IMAGE_QUALITY_HELP} fullRow={qualityAndFormatCount === 1}>
                <Select
                  size="small"
                  fullWidth
                  labelId="ai-image-quality-label"
                  value={draft.quality || 'auto'}
                  onChange={(event) => onQualityChange(String(event.target.value))}
                  sx={selectSx}
                >
                  {(config?.openAiQualities || ['auto', 'low', 'medium', 'high', 'xhigh', 'max']).map((quality) => (
                    <MenuItem key={quality} value={quality}>
                      {formatOpenAiQualityLabel(quality)}
                    </MenuItem>
                  ))}
                </Select>
              </SettingField>
            )}

            {showOutputFormat && (
              <SettingField id="ai-image-format-label" label="Format" fullRow={qualityAndFormatCount === 1}>
                <Select
                  size="small"
                  fullWidth
                  labelId="ai-image-format-label"
                  value={selectedOutputFormat}
                  onChange={(event) => onOutputFormatChange(String(event.target.value))}
                  sx={selectSx}
                >
                  {outputFormats.map((format) => (
                    <MenuItem key={format} value={format}>
                      {getOutputFormatLabel(format, supportsTransparentBackground)}
                    </MenuItem>
                  ))}
                </Select>
              </SettingField>
            )}

          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {costEstimateText && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, px: 0.5 }}>
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', flexShrink: 0 }}>
                Uppskattad kostnad
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary', textAlign: 'right' }}>
                {costEstimateText}
              </Typography>
            </Box>
          )}
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<AutoAwesomeIcon />}
            disabled={Boolean(promptBlockedReason)}
            onClick={onGenerate}
            sx={{
              py: 1.25,
              borderRadius: 2.5,
              fontWeight: 700,
              fontSize: '0.95rem',
              letterSpacing: '0.01em',
              textTransform: 'none',
              background: promptBlockedReason ? undefined : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              boxShadow: promptBlockedReason ? 'none' : '0 6px 16px rgba(15,52,96,0.28)',
              '&:hover': { boxShadow: promptBlockedReason ? 'none' : '0 8px 20px rgba(15,52,96,0.34)' },
            }}
          >
            {generating && !promptBlockedReason ? 'Genererar... (lägg till i kö)' : 'Generera'}
          </Button>
          {promptBlockedReason && (
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 0.5, fontSize: '0.8rem', color: 'text.secondary' }}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              {promptBlockedReason}
            </Typography>
          )}
        </Box>

        {showPreset && (
          <Box>
            <Typography id="ai-image-preset-label" component="div" sx={fieldLabelSx}>
              {presetLabel}
            </Typography>
            <Select
              size="small"
              fullWidth
              displayEmpty
              labelId="ai-image-preset-label"
              value={draft.presetId || ''}
              onChange={(event) => onPresetChange(String(event.target.value))}
              sx={{ ...selectSx, bgcolor: 'background.paper' }}
            >
              <MenuItem value="">Ingen mall</MenuItem>
              {presets.map((preset) => (
                <MenuItem key={preset._id} value={preset._id}>
                  {preset.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
            <Typography component="label" htmlFor="ai-image-prompt" sx={{ ...fieldLabelSx, mb: 0 }}>
              Prompt
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Tooltip title="Maximera prompt">
                <IconButton
                  aria-label="Maximera prompt"
                  aria-expanded={promptExpanded}
                  onClick={() => setPromptExpanded(true)}
                  sx={roundToolButtonSx}
                >
                  <OpenInFullIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {optimizeButton}
            </Box>
          </Box>
          <TextField
            id="ai-image-prompt"
            multiline
            minRows={5}
            maxRows={10}
            value={draft.prompt}
            onChange={handlePromptInput}
            placeholder="Beskriv bilden du vill skapa..."
            helperText={draft.workflow === 'recipe_refresh'
              ? 'Prompten byggs från receptet och vald mall, men kan justeras fritt innan generering.'
              : undefined}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': promptInputRootSx,
              '& textarea': {
                ...promptTextareaSx,
                overflow: 'auto !important',
                maxHeight: 240,
              },
            }}
          />
        </Box>

        {optimizeFeedback}

        {onResetPrompt && (
          <Button
            variant="outlined"
            size="small"
            onClick={onResetPrompt}
            sx={{ alignSelf: 'flex-start', textTransform: 'none', borderRadius: 2 }}
          >
            {resetPromptLabel}
          </Button>
        )}
      </Box>

      {promptExpanded && (
        <FocusTrap open>
          <Box
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-image-prompt-expanded-heading"
            tabIndex={-1}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                setPromptExpanded(false);
              }
            }}
            sx={{
              position: { xs: 'fixed', lg: 'absolute' },
              inset: 0,
              zIndex: { xs: 1300, lg: 5 },
              bgcolor: '#f1f5f9',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              px: { xs: 2, sm: 3 },
              py: 2.5,
              outline: 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  id="ai-image-prompt-expanded-heading"
                  component="h2"
                  sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary' }}
                >
                  Prompt
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  Esc eller Förminska tar dig tillbaka till inställningarna.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                {optimizeButton}
                <Button
                  variant="contained"
                  startIcon={<CloseFullscreenIcon />}
                  onClick={() => setPromptExpanded(false)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 999,
                    px: 2,
                    boxShadow: 'none',
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                  }}
                >
                  Förminska
                </Button>
              </Box>
            </Box>
            <TextField
              multiline
              rows={10}
              value={draft.prompt}
              onChange={handlePromptInput}
              placeholder="Beskriv bilden du vill skapa..."
              inputRef={expandedPromptInputRef}
              slotProps={{ htmlInput: { 'aria-labelledby': 'ai-image-prompt-expanded-heading' } }}
              fullWidth
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                '& .MuiOutlinedInput-root': {
                  ...promptInputRootSx,
                  flex: 1,
                  minHeight: 0,
                  alignItems: 'stretch',
                  fontSize: '0.95rem',
                  lineHeight: 1.65,
                },
                '& textarea': {
                  ...promptTextareaSx,
                  height: '100% !important',
                  overflow: 'auto',
                },
              }}
            />
            {optimizeFeedback}
          </Box>
        </FocusTrap>
      )}
    </Box>
  );
};

export default PromptEditor;
